import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Col, Container } from "reactstrap";
import { approveDeclinePrice, getPrices } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import { FILTER_PAGE } from "utils/constants";
import useStoreFilters from "customHooks/useStoreFilters";
import { isEmpty } from "lodash";

import { makeStyles } from "@material-ui/core/styles";
import classname from "classnames";
import PricesFilter from "pages/price-management/Prices/pricesFilter";
import CustomButton from "components/Common/buttons/CustomButton";
import { pricesColumns } from "pages/price-management/Prices/pricesColumns";
import { PRICE_TYPES_OPTION } from "pages/price-management/Prices/variables";
import PriceTypeFilter from "pages/price-management/Prices/priceTypeFilter";
import { getDictionary } from "store/dictionaries/actions";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import { useDispatch } from "react-redux";
import NewPriceModal from "pages/price-management/Prices/newPrice/NewPriceModal";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";

const useStyles = makeStyles({
  filters: { display: "flex", justifyContent: "space-between" },
  overflowText: {
    overflow: "hidden",
    maxWidth: 110,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
});

const Prices = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activePrice, setActivePrice] = useState(null);
  const [isLoading, setLoader] = useState(false);
  const { filters, setFilters, getFilters } = useStoreFilters(FILTER_PAGE.PRICES);
  const [list, setList] = useState([]);
  const [priceType, setPriceType] = useState(PRICE_TYPES_OPTION.DEFAULT);
  const [step, setStep] = useState(null);
  const [approveDeclineData, setApproveDeclineData] = useState(null);

  const approveDecline = useCallback(
    (item, isApprove) =>
      setApproveDeclineData({
        item,
        isApprove,
      }),
    []
  );
  const approveDeclineClose = useCallback(() => setApproveDeclineData(null), []);
  const onConfirmApproveDecline = useCallback(() => {
    setLoader(true);
    approveDeclinePrice(approveDeclineData.item.id, approveDeclineData.isApprove)
      .then(() => {
        successMessage(`Price was ${approveDeclineData.isApprove ? "approved" : "declined"} successfully`);
        handleFilter(!isEmpty(filters) ? filters : {});
        setLoader(false);
        approveDeclineClose();
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [approveDeclineData, filters]);

  const closeModal = useCallback(() => {
    setActivePrice(null);
    setStep(null);
  }, []);
  const openModal = useCallback(() => setActivePrice({}), []);

  useEffect(() => {
    handleFilter(!isEmpty(filters) ? filters : { type: priceType });
    dispatch(getDictionary(DICTIONARY_TYPES.DEPOT));
    dispatch(getDictionary(DICTIONARY_TYPES.PRODUCT_TYPES));
  }, []);

  const onTypeChange = useCallback(type => {
    setPriceType(type);
    handleFilter({ ...getFilters(), type });
  }, []);

  const handleFilter = useCallback((options = {}) => {
    const { type } = getFilters();
    setFilters({ type, ...options });
    setLoader(true);
    getPrices({ type, ...options })
      .then(res => {
        setList(res?.data || null);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const columns = useMemo(() => pricesColumns(classes, priceType, setActivePrice, approveDecline), [priceType]);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className={classname(classes.filters, "filters-wrapper")}>
            <div className="filter-buttons">
              <CustomButton
                onClick={openModal}
                icon={<i className="bx bx-plus-circle" />}
                type="primary"
                text="NEW PRICE"
              />
            </div>
            <PriceTypeFilter onTypeChange={onTypeChange} />
            <PricesFilter priceType={priceType} onFilter={handleFilter} />
          </div>
          <Col>
            <NGITable
              filtersType={FILTER_PAGE.PRICES}
              columns={columns}
              data={list}
              isLoading={isLoading}
              updateList={handleFilter}
            />
          </Col>
          {activePrice && <NewPriceModal data={activePrice} step={step} onClose={closeModal} update={handleFilter} />}
        </Container>
        {!!approveDeclineData && (
          <ConfirmationModal
            onCancel={approveDeclineClose}
            onConfirm={onConfirmApproveDecline}
            cancelBtnTitle="Cancel"
            confirmBtnTitle="Confirm"
            description={`Are you sure you want to do ${
              approveDeclineData.isApprove ? "approve" : "decline"
            } this price? \n\nPlease confirm!`}
          />
        )}
      </div>
    </>
  );
};

export default Prices;
